<script lang="ts" setup>
import "/custom.css";
import MainLayout from "@/layouts/MainLayout.vue";
import MainContent from "@/components/MainPage/MainContent.vue";
import { computed, ref, watch, onMounted } from "vue";
import client from "@/utils/axios";
import { Status } from "@/types";
import SpinnerLoader from "@/components/Loaders/SpinnerLoader.vue";
import { useUserStore } from "@/stores/user";
import ViewOptionsFilter from "@/components/Commercial/ViewOptionsFilter.vue";
import MainPageHeader from "@/components/PageHeader/MainPageHeader.vue";
import MobileMenu from "@/components/MainPage/MobileMenu.vue";
import { useTranslate } from "@/composables/useTranslate";

const isFilterMenuOpen = ref(false);
const searchQuery = ref<string>("");
const isLoading = ref(false);
const userStore = useUserStore();
const isMobileMenuOpen = ref<boolean>(false);
let currentLanguage = computed(() => userStore.language);
let lastDatabaseTranslationUpdateTimestamp = computed(
  () => userStore.translationsUpdateTimestamp
);
const isFilterSearchFocused = ref<boolean>(false);

const selectedDate = ref(new Date()); // default to today

const todayDatePicker = ref(false);
const weekDatePicker = ref(false);
const isTodayNavigationActive = computed(() => activeFilter.value === "today");
const isWeekNavigationActive = computed(() => activeFilter.value === "week");

// Fetch data
const allStatuses = ref<Status[]>([]);
const aLLProjects = ref<any>([]);
const activeFilter = ref<any>("");
const selectedStatuses = ref<Status[]>([]);
const customerList = ref<any>([]);
const selectedNames = ref<any>([]);
const customerSearchQuery = ref("");
let activeSelectedNames: any = [];
const activeFilterNames = ref();
const loadedFilterNames = ref();
const { t } = useTranslate();

// View options filter
const activeViewOptions = ref<string[]>(
  userStore.viewOptions ? userStore.viewOptions : []
);
const selectedViewOptions = ref<string[]>(
  userStore.viewOptions ? userStore.viewOptions : []
);

const hasNoMatchingCustomers = computed(() => {
  return Object.keys(groupedCustomers.value).length === 0;
});

const onUpdateViewOpton = (options: string[]) => {
  selectedViewOptions.value = options;
};

const getAllStatuses = async () => {
  try {
    const response = await client.get(
      "/project-statuses?locale=" + currentLanguage.value
    );

    if (response.data) {
      let statuses = response.data.map((item: any) => {
        return {
          ...item,
          isSelected: true,
          isOpen: true,
        };
      });

      const userFilters = userStore.statusFilters;
      statuses = statuses.map((status: any) => {
        if (userFilters.includes(status.name)) {
          status.isSelected = true;
        } else {
          status.isSelected = false;
        }

        return status;
      });

      allStatuses.value = statuses;

      selectedStatuses.value = statuses.filter(
        (status: any) => status.isSelected
      );

      loadedFilterNames.value = statuses.filter(
        (status: any) => status.isSelected
      );
    }
  } catch (error) {
    console.log(error);
  }
};

const getActiveFilter = async () => {
  try {
    if (userStore.filter) {
      if (/^\d{4}-\d{2}-\d{2}$/.test(userStore.filter)) {
        todayDatePicker.value = true;
        selectedDate.value = new Date(userStore.filter);
        activeFilter.value = "today";
      } else if (
        /^\d{4}-\d{2}-\d{2}_to_\d{4}-\d{2}-\d{2}$/.test(userStore.filter)
      ) {
        const [startStr, endStr] = userStore.filter.split("_to_");
        const start = new Date(startStr);
        const end = new Date(endStr);

        if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
          weekDatePicker.value = true;
          selectedStartDate.value = start;
          selectedEndDate.value = end;
          activeFilter.value = "week";
        }

        // Fallback: simple string filter (like "month", "all", etc.)
      } else {
        activeFilter.value = userStore.filter;
      }
    }
  } catch (error) {
    console.log(error);
  }
};

const getActiveSearchFilter = async () => {
  try {
    if (userStore.searchFilter) {
      searchQuery.value = userStore.searchFilter;
    }
  } catch (error) {
    console.log(error);
  }
};

const getActiveNameFilter = async () => {
  try {
    if (userStore.nameFilter) {
      activeSelectedNames = userStore.nameFilter;
      selectedNames.value = userStore.nameFilter;
    }
  } catch (error) {
    console.log(error);
  }
};

const getAllProjects = async () => {
  isLoading.value = true;

  try {
    const response = await client.get("/projects");
    if (response.data) {
      aLLProjects.value = response.data;
    }
    const seen = new Set();
    customerList.value = aLLProjects.value
      .map((project: any) => {
        const customer = project.customer;
        if (!customer?.name) return null;
        return {
          id: customer.id,
          name: customer.name.trim(),
          logo_url: customer.logo_url,
          key: customer.name.trim().toLowerCase(),
        };
      })
      .filter((customer: any) => {
        if (!customer || seen.has(customer.key)) return false;
        seen.add(customer.key);
        return true;
      })
      .sort((a: any, b: any) => a.name.localeCompare(b.name));
  } catch (error) {
    console.log(error);
  } finally {
    isLoading.value = false;
  }
};

const groupedCustomers = computed(() => {
  const groups = {};
  const query = customerSearchQuery.value.trim().toLowerCase();

  for (const customer of customerList.value) {
    // Only include if name matches search
    if (!query || customer.name.toLowerCase().includes(query)) {
      const letter = customer.name[0].toUpperCase();
      if (!groups[letter]) groups[letter] = [];
      groups[letter].push(customer);
    }
  }

  return groups;
});

const removeCustomer = (name: any) => {
  selectedNames.value = selectedNames.value.filter(
    (selectedName: any) => selectedName !== name
  );
};

const toggleStatusOpen = (statusId: number) => {
  const status = allStatuses.value.find(
    (status: any) => status.id === statusId
  );

  if (status) {
    status.isOpen = !status.isOpen;
  }
};

onMounted(() => {
  getAllStatuses();
  getAllProjects();
  getActiveFilter();
  getActiveSearchFilter();
  getActiveNameFilter();
});

const filteredProjects = computed(() => {
  let results = aLLProjects.value;
  switch (activeFilter.value) {
    case "today": {
      const selected = new Date(selectedDate.value);
      selected.setHours(0, 0, 0, 0); // normalize to start of day

      results = results.filter((project: any) => {
        let match = false;

        project.termins?.forEach((termin: any) => {
          let start = termin.start ? new Date(termin.start) : null;
          let end = termin.end ? new Date(termin.end) : null;

          if (start) start.setHours(0, 0, 0, 0);
          if (end) end.setHours(0, 0, 0, 0);

          const isStartToday = start && start.getTime() === selected.getTime();
          const isEndToday = end && end.getTime() === selected.getTime();
          const isOngoing =
            start &&
            end &&
            start.getTime() < selected.getTime() &&
            end.getTime() > selected.getTime();

          if (isStartToday || isEndToday || isOngoing) {
            match = true;
          }
        });

        return match;
      });
      break;
    }
    case "week": {
      const monday = new Date(selectedStartDate.value);
      const sunday = new Date(selectedEndDate.value);

      // Just to be sure: normalize the range
      monday.setHours(0, 0, 0, 0);
      sunday.setHours(23, 59, 59, 999);

      results = results.filter((project: any) => {
        let match = false;

        project.termins?.forEach((termin: any) => {
          let start: Date | null = null;
          let end: Date | null = null;

          if (termin.start) {
            start = new Date(termin.start);
            start.setHours(0, 0, 0, 0);
          }

          if (termin.end) {
            end = new Date(termin.end);
            end.setHours(0, 0, 0, 0);
          }

          const projectStartsThisWeek = start
            ? start >= monday && start <= sunday
            : false;

          const projectEndsThisWeek = end
            ? end >= monday && end <= sunday
            : false;

          const projectInProgress =
            start && end ? start < sunday && end > monday : false;

          if (
            projectStartsThisWeek ||
            projectEndsThisWeek ||
            projectInProgress
          ) {
            match = true;
          }
        });

        return match;
      });
      break;
    }
    case "abnahme":
      results = results.filter((project: any) => {
        return (
          project.phases?.filter((item: any) => item.status.name === "abnahme")
            .length > 0
        );
      });
      break;
    case "qc":
      results = results.filter((project: any) => {
        return (
          project.phases?.filter((item: any) => item.status.name === "qc")
            .length > 0
        );
      });
      break;
  }

  if (searchQuery.value && searchQuery.value.length > 0) {
    results = results.filter((query: any) => {
      return query.name.toLowerCase().includes(searchQuery.value.toLowerCase());
    });
  }

  return results;
});

function calculateWeek(base: Date) {
  const d = new Date(base);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay(); // 0 (Sun) – 6 (Sat)
  const diff = day === 0 ? -6 : 1 - day;
  const monday = new Date(d);
  monday.setDate(d.getDate() + diff);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return { monday, sunday };
}

const { monday, sunday } = calculateWeek(new Date());
const selectedStartDate = ref(monday);
const selectedEndDate = ref(sunday);

const setActiveFilter = (filter: string) => {
  if (activeFilter.value === filter) {
    activeFilter.value = null;
  } else {
    activeFilter.value = filter;
  }
  onSaveSelectedStatuses();
};

const handleSaveClick = () => {
  onSaveSelectedStatuses();
  isFilterMenuOpen.value = !isFilterMenuOpen.value;
};

function formatDate(date: Date) {
  const safeDate = new Date(date);
  safeDate.setHours(12, 0, 0, 0); // Set time to 12:00 to avoid UTC conversion issues

  const year = safeDate.getFullYear();
  const month = String(safeDate.getMonth() + 1).padStart(2, "0");
  const day = String(safeDate.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

const onSaveSelectedStatuses = () => {
  selectedStatuses.value = allStatuses.value.filter(
    (status: any) => status.isSelected
  );

  loadedFilterNames.value = allStatuses.value.filter(
    (status: any) => status.isSelected
  );

  // make API request to save selected statuses
  activeFilterNames.value = selectedStatuses.value.map(
    (status: any) => status.name
  );

  const todayFormatted = formatDate(selectedDate.value);
  const weekFormatted = `${formatDate(selectedStartDate.value)}_to_${formatDate(
    selectedEndDate.value
  )}`;

  let activeFilterName;

  if (
    todayDatePicker.value === true &&
    isTodayNavigationActive.value === true
  ) {
    activeFilterName = todayFormatted;
  } else if (
    weekDatePicker.value === true &&
    isWeekNavigationActive.value === true
  ) {
    activeFilterName = weekFormatted;
  } else {
    activeFilterName = activeFilter.value;
  }

  const activeSearchQuery = searchQuery.value;

  activeViewOptions.value = selectedViewOptions.value;

  activeSelectedNames = selectedNames.value;

  client.post(`/user/save-filters`, {
    statusFilters: activeFilterNames.value,
    filter: activeFilterName,
    searchFilter: activeSearchQuery,
    viewOptions: activeViewOptions.value,
    nameFilters: activeSelectedNames,
  });

  userStore.setStatusFilters(activeFilterNames.value);
  userStore.setFilter(activeFilterName);
  userStore.setSearchFilter(activeSearchQuery);
  userStore.setViewOption(activeViewOptions.value);
  userStore.setNameFilter(activeSelectedNames);
};

const closeFilterWindow = () => {
  isFilterMenuOpen.value = false;

  const selectedIds = new Set(loadedFilterNames.value.map((item) => item.id));

  allStatuses.value = allStatuses.value.map((item) => ({
    ...item,
    isSelected: selectedIds.has(item.id),
  }));

  selectedNames.value = activeSelectedNames;

  selectedViewOptions.value = activeViewOptions.value;
};

const onResetSelectedStatuses = () => {
  allStatuses.value.forEach((status: any) => {
    status.isSelected = false;
  });

  selectedNames.value = [];

  selectedViewOptions.value = [];
};

const goToPreviousDay = () => {
  todayDatePicker.value = true;
  const newDate = new Date(selectedDate.value);
  newDate.setDate(newDate.getDate() - 1);
  selectedDate.value = newDate;

  onSaveSelectedStatuses();
};

const goToNextDay = () => {
  todayDatePicker.value = true;
  const newDate = new Date(selectedDate.value);
  newDate.setDate(newDate.getDate() + 1);
  selectedDate.value = newDate;

  onSaveSelectedStatuses();
};

const resetToToday = () => {
  todayDatePicker.value = false;
  selectedDate.value = new Date();

  onSaveSelectedStatuses();
};

function goToPreviousWeek() {
  weekDatePicker.value = true;
  const { monday } = calculateWeek(selectedStartDate.value);
  const prevBase = new Date(monday);
  prevBase.setDate(monday.getDate() - 7);
  const { monday: m, sunday: s } = calculateWeek(prevBase);
  selectedStartDate.value = m;
  selectedEndDate.value = s;
  onSaveSelectedStatuses();
}

function goToNextWeek() {
  weekDatePicker.value = true;
  const { monday } = calculateWeek(selectedStartDate.value);
  const nextBase = new Date(monday);
  nextBase.setDate(monday.getDate() + 7);
  const { monday: m, sunday: s } = calculateWeek(nextBase);
  selectedStartDate.value = m;
  selectedEndDate.value = s;
  onSaveSelectedStatuses();
}

function resetToThisWeek() {
  weekDatePicker.value = false;
  const { monday, sunday } = calculateWeek(new Date());
  selectedStartDate.value = monday;
  selectedEndDate.value = sunday;
  onSaveSelectedStatuses();
}
const onChangeSearch = (value: string) => (searchQuery.value = value);

// Debaunce Timer
let debounceTimer: any;
watch(searchQuery, () => {
  clearTimeout(debounceTimer); // Clear any existing timer
  debounceTimer = setTimeout(() => {
    onSaveSelectedStatuses(); // Call function
  }, 1500);
});

watch(
  currentLanguage,
  async (newLang: any, oldLang: any) => {
    if (newLang && oldLang && newLang !== oldLang) {
      await getAllStatuses();
    }
  },
  { immediate: true }
);

watch(
  lastDatabaseTranslationUpdateTimestamp,
  async (newTimestamp: any, oldTimestamp: any) => {
    if (newTimestamp && oldTimestamp && newTimestamp !== oldTimestamp) {
      await getAllStatuses();
    }
  },
  { immediate: true }
);
</script>
<template>
  <MainLayout>
    <!-- Desktop menu > 768px -->
    <div class="w-full hidden md:flex">
      <MainPageHeader
        :is-today-navigation-active="isTodayNavigationActive"
        :today-date-picker="todayDatePicker"
        :selected-date="selectedDate"
        :is-week-navigation-active="isWeekNavigationActive"
        :week-date-picker="weekDatePicker"
        :selected-start-date="selectedStartDate"
        :selected-end-date="selectedEndDate"
        :active-filter="activeFilter"
        :search-query="searchQuery"
        :is-loading="isLoading"
        :active-view-list="activeViewOptions"
        :active-status-list="selectedStatuses"
        :active-names-list="activeSelectedNames"
        :is-filter-open="isFilterMenuOpen"
        @goToPreviousDay="goToPreviousDay"
        @goToNextDay="goToNextDay"
        @resetToToday="resetToToday"
        @goToPreviousWeek="goToPreviousWeek"
        @goToNextWeek="goToNextWeek"
        @resetToThisWeek="resetToThisWeek"
        @setActiveFilter="setActiveFilter"
        @on-change-search="onChangeSearch"
        @get-all-projects="getAllProjects"
        @toggle-filter-menu="isFilterMenuOpen = !isFilterMenuOpen"
      />
    </div>
    <!-- Mobile menu < 768px -->
    <div class="w-full flex md:hidden">
      <div
        class="w-full flex flex-col bg-[#E5E4E2] mt-4 mx-4 p-3 items-center justify-between rounded"
      >
        <div class="w-full flex items-center justify-between rounded">
          <RouterLink
            to="/"
            class="w-[24px] h-[24px] flex items-center justify-center cursor-pointer"
          >
            <img src="@/assets/smalt-home-logo.svg" alt="Smalt icon" />
          </RouterLink>
          <button
            v-if="!isMobileMenuOpen"
            @click="isMobileMenuOpen = !isMobileMenuOpen"
            class="w-8 h-8 flex items-center justify-center"
          >
            <img src="@/assets/hamburger-menu.svg" alt="Smalt icon" />
          </button>
          <button
            v-else
            class="w-8 h-8 flex items-center justify-center"
            @click="isMobileMenuOpen = !isMobileMenuOpen"
          >
            <img src="@/assets/close-icon-2.svg" alt="Smalt icon" />
          </button>
        </div>
        <div v-if="isMobileMenuOpen" class="w-full">
          <MobileMenu
            :is-today-navigation-active="isTodayNavigationActive"
            :today-date-picker="todayDatePicker"
            :selected-date="selectedDate"
            :is-week-navigation-active="isWeekNavigationActive"
            :week-date-picker="weekDatePicker"
            :selected-start-date="selectedStartDate"
            :selected-end-date="selectedEndDate"
            :active-filter="activeFilter"
            :search-query="searchQuery"
            :is-loading="isLoading"
            :active-view-list="activeViewOptions"
            :active-status-list="selectedStatuses"
            :active-names-list="selectedNames"
            @goToPreviousDay="goToPreviousDay"
            @goToNextDay="goToNextDay"
            @resetToToday="resetToToday"
            @goToPreviousWeek="goToPreviousWeek"
            @goToNextWeek="goToNextWeek"
            @resetToThisWeek="resetToThisWeek"
            @setActiveFilter="setActiveFilter"
            @on-change-search="onChangeSearch"
            @get-all-projects="getAllProjects"
          />
        </div>
      </div>
    </div>
    <div class="flex items-start">
      <div class="w-full flex flex-col items-center">
        <!-- Header -->
        <div
          class="w-full flex flex-col lg:flex-row items-center justify-center lg:items-start lg:justify-between"
        >
          <!-- Filter Menu -->
          <div class="flex items-center gap-2 relative cursor-default">
            <div class="custom-select">
              <Transition name="nested">
                <div
                  v-if="isFilterMenuOpen"
                  class="fixed top-0 right-0 bottom-0 left-0 bg-black/35 z-20 overflow-y-auto"
                >
                  <div>
                    <div
                      class="filter w-[540px] max-h-[calc(100vh-55px)] absolute flex flex-col translate-y-[50px] right-[60px] z-50 min-w-[170px] bg-white p-[24px] rounded-lg shadow-sm gap-[12px] cursor-default"
                    >
                      <div class="flex items-start justify-between">
                        <p class="text-gray-600">{{ t("home.filter_by") }}</p>

                        <img
                          @click="closeFilterWindow()"
                          src="@/assets/close-icon-2.svg "
                          alt="Close"
                          class="cursor-pointer"
                        />
                      </div>
                      <ViewOptionsFilter
                        :selected-view-options="selectedViewOptions"
                        @on-option-update="onUpdateViewOpton"
                      />
                      <div class="border-b border-gray-200">
                        <div class="flex items-start justify-between pb-[16px]">
                          <h4
                            class="text-base leading-[20px] text-primary-text-color tracking-[0.01rem] font-semibold"
                          >
                            {{ t("home.by_status") }}
                            <span
                              v-if="
                                allStatuses.filter(
                                  (status) => status.isSelected
                                ).length > 0
                              "
                            >
                              ({{
                                allStatuses.filter(
                                  (status) => status.isSelected
                                ).length
                              }})
                            </span>
                          </h4>
                        </div>
                        <div
                          class="flex flex-wrap gap-[8px] overflow-y-auto pb-[16px]"
                        >
                          <div v-for="column in allStatuses" :key="column.id">
                            <button
                              @click="column.isSelected = !column.isSelected"
                              :class="[
                                'px-[10px] py-[10px] rounded-[14px] whitespace-nowrap text-sm',
                                column.isSelected
                                  ? 'bg-[#D1CFF7] text-gray-800'
                                  : 'bg-gray-200 text-gray-800',
                              ]"
                            >
                              {{ column.name }}
                            </button>
                          </div>
                        </div>
                      </div>
                      <div class="flex items-start justify-between">
                        <h4
                          class="text-lg leading-[20px] text-primary-text-color tracking-[0.01rem] font-semibold"
                        >
                          {{ t("home.by_customer") }}
                          <span v-if="selectedNames.length > 0">
                            ({{ selectedNames.length }})
                          </span>
                        </h4>
                      </div>
                      <div class="flex flex-col h-[453px] overflow-y-auto">
                        <!-- Selected Names -->
                        <div class="flex flex-wrap gap-2 mb-[16px]">
                          <div
                            v-for="name in selectedNames"
                            :key="name"
                            class="flex items-center text-sm bg-[#E8E7FB] text-gray-800 px-[8px] py-[2px] rounded-[7px]"
                          >
                            <span class="text-[#443FA0]">
                              {{
                                customerList.find((c: any) => c.name === name)
                                  ?.name
                              }}
                            </span>
                            <button
                              class="ml-2 text-[#7F7ADC] focus:outline-none"
                              @click="removeCustomer(name)"
                            >
                              &times;
                            </button>
                          </div>
                        </div>
                        <!-- Search - filter -->
                        <div class="flex items-center gap-3 pb-[20px]">
                          <!-- Search -->
                          <form
                            class="w-full h-[30px] rounded-primary-radius px-[10px] border bg-transparent flex items-center hover:bg-[#D3D1CD] gap-1"
                            :class="`${
                              isFilterSearchFocused
                                ? 'border-[#393840]'
                                : 'border-[#A7A5AE]'
                            }`"
                          >
                            <div v-if="!isFilterSearchFocused">
                              <img
                                class="w-4 h-4"
                                src="@/assets/search-redesign.svg"
                                alt="Search"
                              />
                            </div>
                            <div v-if="isFilterSearchFocused">
                              <img
                                class="w-4 h-4"
                                src="@/assets/search-redesign-black.svg"
                                alt="Search"
                              />
                            </div>
                            <input
                              @focus="isFilterSearchFocused = true"
                              @blur="isFilterSearchFocused = false"
                              class="w-full bg-transparent outline-none text-sm font-normal placeholder:text-[#898790]"
                              :class="`${
                                isFilterSearchFocused
                                  ? 'text-[#393840]'
                                  : 'text-[#898790]'
                              }`"
                              type="text"
                              v-model="customerSearchQuery"
                              :placeholder="t('home.search').value"
                            />
                          </form>
                        </div>
                        <p
                          v-if="customerSearchQuery"
                          class="mb-[8px] text-[#898790]"
                        >
                          {{ t("global.search_results") }}
                        </p>
                        <div
                          v-if="!hasNoMatchingCustomers"
                          class="max-h-[319px] overflow-y-auto"
                        >
                          <div
                            v-for="(customers, letter) in groupedCustomers"
                            :key="letter"
                          >
                            <h2
                              v-if="!customerSearchQuery"
                              class="text-[#7F7ADC] text-[10px] mb-[8px]"
                            >
                              {{ letter }}
                            </h2>
                            <div>
                              <label
                                v-for="customer in customers"
                                :key="customer.key"
                                class="flex items-center border-b border-gray-200 p-[4px] gap-[8px] mb-[4px]"
                              >
                                <input
                                  type="checkbox"
                                  :value="customer.name"
                                  v-model="selectedNames"
                                  :class="[
                                    selectedNames.includes(customer.name)
                                      ? 'projects-filter-checkbox-checked'
                                      : 'projects-filter-checkbox',
                                  ]"
                                />
                                <img
                                  v-if="customer.logo_url"
                                  :src="customer.logo_url"
                                  alt="Logo"
                                  class="w-full h-[25px] object-contain object-left"
                                />
                                <span v-else>{{ customer.name }}</span>
                              </label>
                            </div>
                          </div>
                        </div>
                        <div
                          v-else
                          class="h-[319px] flex flex-col items-center py-[40px] text-gray-400 text-sm"
                        >
                          <img src="../assets/no-cloud-icon.svg" alt="" />
                          <p class="mt-[12px]">
                            {{ t("home.no_search_results") }}
                          </p>
                        </div>
                      </div>
                      <!-- Controls -->
                      <div class="flex items-center justify-between">
                        <button
                          @click="onResetSelectedStatuses"
                          class="text-base leading-5 tracking-[0.01rem] text-primary-text-color font-semibold p-2 rounded-primary-radius border-2 border-[#393840]"
                        >
                          {{ t("home.clear_all") }}
                        </button>
                        <button
                          @click="handleSaveClick"
                          class="text-base leading-5 tracking-[0.01rem] text-white font-semibold p-2 rounded-primary-radius border-2 border-[#393840] bg-[#393840]"
                        >
                          {{ t("home.apply_filters") }}
                          <span
                            v-if="
                              selectedViewOptions.length > 0 ||
                              allStatuses.filter((status) => status.isSelected)
                                .length > 0 ||
                              selectedNames.length > 0
                            "
                          >
                            ({{
                              selectedViewOptions.length +
                              allStatuses.filter((status) => status.isSelected)
                                .length +
                              selectedNames.length
                            }})
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </Transition>
            </div>
          </div>
        </div>
        <div v-if="isLoading" class="mt-28">
          <SpinnerLoader />
        </div>
        <!-- TODO: Is it items start? For what screen if at all? -->
        <div
          class="h-[calc(100vh-125px)] overflow-y-auto self-center max-[1507px]:w-full max-[1507px]:items-start p-4"
          v-else
        >
          <MainContent
            :selected-view-options="activeViewOptions"
            :status-list="selectedStatuses"
            :all-statuses="allStatuses"
            :project-list="filteredProjects"
            :name-filter="activeSelectedNames"
            @toggle-status-open="toggleStatusOpen"
          />
        </div>
      </div>
    </div>
  </MainLayout>
</template>

<style scoped>
.nested-enter-active,
.nested-leave-active {
  transition: opacity 0.3s ease;
}

.nested-enter-from,
.nested-leave-to {
  opacity: 0;
}

.nested-enter-active .filter,
.nested-leave-active .filter {
  transition: transform 0.3s ease;
}

.nested-enter-from .filter,
.nested-leave-to .filter {
  transform: translateY(0);
}

.nested-enter-to .filter,
.nested-leave-from .filter {
  transform: translateY(75px);
}
</style>
